import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';

const errText = 'font-dm-sans text-sm text-white/55 font-light leading-relaxed';
const hintText = 'font-dm-sans text-[13px] text-white/40 font-light leading-relaxed';

// Partner invite-to-portal activation screen. An invited partner lands here from
// the invite email link (admin.inviteUserByEmail → redirectTo /partner/set-
// password), sets a password, and activates their account. This is their FIRST
// login (an activation, not a reset). Uses the isolated supabasePartner client
// only; never the anon customer client.
export default function PartnerSetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // 'checking' while we wait for the invite session to be established from the
  // URL token, then 'valid' or 'invalid'. Direct visits (no token) and expired
  // links resolve to 'invalid'.
  const [sessionState, setSessionState] = useState('checking');

  useEffect(() => {
    let active = true;

    // SECURITY: the gate is "did the user arrive via a genuine invite link?" —
    // NOT "is anyone logged in?". A normal, already-authenticated partner session
    // must NEVER unlock this form, otherwise a logged-in user (or anyone on their
    // machine) could set/overwrite a password by visiting /partner/set-password
    // directly. So we deliberately do not use getSession()/getUser() as the gate.
    // Only two signals count, both tied to a real invite link processed by
    // detectSessionInUrl:
    //   a) the URL hash carries an invite/recovery token marker, and
    //   b) Supabase fires the corresponding auth event (SIGNED_IN for invites,
    //      PASSWORD_RECOVERY for recovery-style links).
    const hash = window.location.hash;
    const hasInviteToken =
      hash.includes('type=invite') ||
      hash.includes('type=recovery') ||
      hash.includes('access_token');

    // Authoritative signal: Supabase processed the invite/recovery link.
    const { data: sub } = supabasePartner.auth.onAuthStateChange((event) => {
      if (!active) return;
      if (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') setSessionState('valid');
    });

    if (!hasInviteToken) {
      // No token in the URL — this is a direct visit. Block it even if a normal
      // session already exists. (Guard against an auth event still arriving by
      // only downgrading from 'checking'.)
      setSessionState(prev => (prev === 'checking' ? 'invalid' : prev));
    } else {
      // A token is present; give detectSessionInUrl a brief moment to process it
      // and fire the auth event. If nothing arrives, treat the link as invalid/
      // expired rather than showing the form.
      setTimeout(() => {
        if (!active) return;
        setSessionState(prev => (prev === 'checking' ? 'invalid' : prev));
      }, 1500);
    }

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async () => {
    const next = {};
    if (password.length < 6) next.password = 'Password must be at least 6 characters';
    if (confirmPassword !== password) next.confirmPassword = 'Passwords do not match';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const { error } = await supabasePartner.auth.updateUser({ password });
      if (error) {
        // No valid invite session (expired/invalid link, or direct visit) —
        // updateUser fails because there's no authenticated user to update.
        setSessionState('invalid');
        return;
      }
      setDone(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Portal
        </p>

        {done ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Account activated
            </h1>
            <p className="font-dm-sans text-sm text-white/70 font-light mt-3 leading-relaxed">
              Your account is active. You can now sign in with your new password.
            </p>
            <Link
              to="/partner/login"
              className="block w-full text-center mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
            >
              Go to sign in
            </Link>
          </>
        ) : sessionState === 'invalid' ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Invitation expired
            </h1>
            <p className="font-dm-sans text-sm text-white/70 font-light mt-3 leading-relaxed">
              This invitation link is invalid or has expired. Please contact Alfresco Grand for a new invite.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Welcome to the Alfresco Grand Partner Portal
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mb-7 leading-relaxed">
              Set a password to activate your account.
            </p>

            <div className="flex flex-col gap-5">

              <div className="group flex flex-col gap-2">
                <label className={labelBase}>Password</label>
                <div className={borderBase}>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    className={inputBase}
                    autoComplete="new-password"
                  />
                </div>
                {errors.password ? (
                  <p className={errText}>{errors.password}</p>
                ) : (
                  <p className={hintText}>At least 6 characters.</p>
                )}
              </div>

              <div className="group flex flex-col gap-2">
                <label className={labelBase}>Confirm Password</label>
                <div className={borderBase}>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    className={inputBase}
                    autoComplete="new-password"
                  />
                </div>
                {errors.confirmPassword ? (
                  <p className={errText}>{errors.confirmPassword}</p>
                ) : (
                  <p className={hintText}>Re-enter your password</p>
                )}
              </div>

            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || sessionState === 'checking'}
              className="w-full mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              {submitting ? 'Activating…' : 'Set password & activate'}
            </button>

            {submitError && (
              <p className="font-dm-sans text-center text-sm text-white/55 mt-3.5 leading-relaxed">
                {submitError}
              </p>
            )}
          </>
        )}

        <div className="mt-6 pt-4 border-t border-white/[0.07] text-center">
          <Link
            to="/"
            className="font-dm-sans text-[13px] text-white/40 hover:text-[rgba(201,168,76,0.85)] transition-colors duration-300"
          >
            ← Back to main site
          </Link>
        </div>

      </div>
    </div>
  );
}
